import { backendURL, buildAuthOpts } from "../components/backendConfig";
import { getUserExternalMcpConnectorsForBackend } from "./mcpConnectionPrefs";

const MCP_SNAPSHOT_MAX_CLIENT_BODY_BYTES = 8 * 1024 * 1024;
const MCP_TRANSPORT_TEXT_LIMIT = 1200;
const MCP_TRANSPORT_ARRAY_LIMIT = 80;
const MCP_TRANSPORT_DEEP_ARRAY_LIMIT = 20;

function byteSize(value) {
  if (typeof Blob !== "undefined") return new Blob([value]).size;
  return value.length;
}

function compactTransportValue(value, key = "", depth = 0) {
  if (value == null || typeof value === "number" || typeof value === "boolean") return value;
  if (typeof value === "string") {
    if (/dataUrl|base64/i.test(key)) return "[omitted from MCP transport snapshot]";
    const limit = /unifiedDiff|diff|patch|markdown|stdout|stderr|log|trace|source/i.test(key)
      ? MCP_TRANSPORT_TEXT_LIMIT
      : 600;
    return value.length > limit ? `${value.slice(0, limit)}\n...[truncated for MCP transport snapshot]` : value;
  }
  if (Array.isArray(value)) {
    const limit = depth >= 2 ? MCP_TRANSPORT_DEEP_ARRAY_LIMIT : MCP_TRANSPORT_ARRAY_LIMIT;
    return value.slice(0, limit).map((item) => compactTransportValue(item, key, depth + 1));
  }
  if (typeof value === "object") {
    if (depth > 5) return "[truncated for MCP transport snapshot depth]";
    return Object.fromEntries(
      Object.entries(value).map(([childKey, childValue]) => [
        childKey,
        compactTransportValue(childValue, childKey, depth + 1),
      ])
    );
  }
  return String(value);
}

function compactProjectSamples(project = {}) {
  const samples = project.samples || {};
  return {
    ...project,
    samples: Object.fromEntries(
      Object.entries(samples).map(([key, rows]) => [
        key,
        Array.isArray(rows) ? rows.slice(0, 3).map((row) => compactTransportValue(row, key, 1)) : compactTransportValue(rows, key, 1),
      ])
    ),
  };
}

function compactCodeArchitectureWorkspace(codeArchitecture = {}) {
  if (!codeArchitecture || typeof codeArchitecture !== "object") return codeArchitecture;
  return {
    ...codeArchitecture,
    legacyRows: [],
    projects: (Array.isArray(codeArchitecture.projects) ? codeArchitecture.projects : []).map((project) => ({
      id: project.id,
      name: project.name,
      folder: project.folder || null,
      activeRepoId: project.activeRepoId || null,
      repos: (Array.isArray(project.repos) ? project.repos : []).map((repo) => ({
        id: repo.id,
        repoId: repo.repoId,
        repoName: repo.repoName,
        repoUrl: repo.repoUrl,
        branch: repo.branch,
        commitSha: repo.commitSha,
        active: Boolean(repo.active),
        rowCount: repo.rowCount || 0,
        publishedRowCount: Math.min(repo.publishedRowCount || 0, 20),
        rowLimit: 20,
        rowsTruncated: true,
        updatedAt: repo.updatedAt || null,
        sourceKey: repo.sourceKey || "",
        rows: (Array.isArray(repo.rows) ? repo.rows : []).slice(0, 20).map((row) => compactTransportValue(row, "codeArchitectureRow", 1)),
        hazardRuns: (Array.isArray(repo.hazardRuns) ? repo.hazardRuns : []).slice(0, 20).map((run) => compactTransportValue(run, "hazardRun", 1)),
        safetyRemediation: compactTransportValue(repo.safetyRemediation || {}, "safetyRemediation", 1),
        assuranceArtifacts: compactTransportValue(repo.assuranceArtifacts || {}, "assuranceArtifacts", 1),
      })),
    })),
  };
}

function compactMcpSnapshotForTransport(snapshot = {}) {
  const workspace = snapshot.workspace || {};
  const knownKeys = new Set([
    "project",
    "focus",
    "projectHint",
    "appFocus",
    "scope",
    "mcpPublishScope",
    "requirements",
    "functionalDecomposition",
    "riskRegister",
    "riskSummarySheet",
    "vnvArtifacts",
    "codeArchitecture",
    "liteSummaries",
    "diagramSnapshots",
    "workspace",
    "sourcesMeta",
  ]);
  const compactedExtras = Object.fromEntries(
    Object.entries(snapshot)
      .filter(([key]) => !knownKeys.has(key))
      .map(([key, value]) => [key, compactTransportValue(value, key, 1)])
  );
  return {
    ...compactedExtras,
    mcpSnapshotTransportCompacted: true,
    codeArchitecture: [],
    project: compactTransportValue(snapshot.project, "project", 1),
    focus: compactTransportValue(snapshot.focus, "focus", 1),
    projectHint: compactTransportValue(snapshot.projectHint, "projectHint", 1),
    appFocus: compactTransportValue(snapshot.appFocus, "appFocus", 1),
    scope: snapshot.scope,
    mcpPublishScope: snapshot.mcpPublishScope,
    requirements: Array.isArray(snapshot.requirements) ? snapshot.requirements.slice(0, 200).map((row) => compactTransportValue(row, "requirement", 1)) : compactTransportValue(snapshot.requirements, "requirements", 1),
    functionalDecomposition: Array.isArray(snapshot.functionalDecomposition) ? snapshot.functionalDecomposition.slice(0, 200).map((row) => compactTransportValue(row, "functionalDecomposition", 1)) : compactTransportValue(snapshot.functionalDecomposition, "functionalDecomposition", 1),
    riskRegister: Array.isArray(snapshot.riskRegister) ? snapshot.riskRegister.slice(0, 200).map((row) => compactTransportValue(row, "riskRegister", 1)) : compactTransportValue(snapshot.riskRegister, "riskRegister", 1),
    riskSummarySheet: Array.isArray(snapshot.riskSummarySheet) ? snapshot.riskSummarySheet.slice(0, 200).map((row) => compactTransportValue(row, "riskSummarySheet", 1)) : compactTransportValue(snapshot.riskSummarySheet, "riskSummarySheet", 1),
    vnvArtifacts: snapshot.vnvArtifacts ? {
      ...compactTransportValue(snapshot.vnvArtifacts, "vnvArtifacts", 1),
      tests: Array.isArray(snapshot.vnvArtifacts.tests) ? snapshot.vnvArtifacts.tests.slice(0, 120).map((row) => compactTransportValue(row, "test", 1)) : compactTransportValue(snapshot.vnvArtifacts.tests, "tests", 1),
      testCases: Array.isArray(snapshot.vnvArtifacts.testCases) ? snapshot.vnvArtifacts.testCases.slice(0, 120).map((row) => compactTransportValue(row, "testCase", 1)) : compactTransportValue(snapshot.vnvArtifacts.testCases, "testCases", 1),
    } : snapshot.vnvArtifacts,
    liteSummaries: Array.isArray(snapshot.liteSummaries) ? snapshot.liteSummaries.slice(0, 40).map((row) => compactTransportValue(row, "liteSummary", 1)) : compactTransportValue(snapshot.liteSummaries, "liteSummaries", 1),
    diagramSnapshots: Array.isArray(snapshot.diagramSnapshots) ? snapshot.diagramSnapshots.slice(0, 40).map((row) => compactTransportValue(row, "diagramSnapshot", 1)) : compactTransportValue(snapshot.diagramSnapshots, "diagramSnapshots", 1),
    sourcesMeta: compactTransportValue(snapshot.sourcesMeta, "sourcesMeta", 1),
    workspace: {
      ...workspace,
      projects: (Array.isArray(workspace.projects) ? workspace.projects : []).map(compactProjectSamples),
      legacyCodeArchitectureRows: [],
      codeArchitecture: compactCodeArchitectureWorkspace(workspace.codeArchitecture),
      sysmlModels: Array.isArray(workspace.sysmlModels) ? workspace.sysmlModels.slice(0, 40).map((row) => compactTransportValue(row, "sysmlModel", 1)) : compactTransportValue(workspace.sysmlModels, "sysmlModels", 1),
      liteSummaries: Array.isArray(workspace.liteSummaries) ? workspace.liteSummaries.slice(0, 40).map((row) => compactTransportValue(row, "liteSummary", 1)) : compactTransportValue(workspace.liteSummaries, "liteSummaries", 1),
      diagramSnapshots: Array.isArray(workspace.diagramSnapshots) ? workspace.diagramSnapshots.slice(0, 40).map((row) => compactTransportValue(row, "diagramSnapshot", 1)) : compactTransportValue(workspace.diagramSnapshots, "diagramSnapshots", 1),
      codeArchitectureHazardRuns: Array.isArray(workspace.codeArchitectureHazardRuns) ? workspace.codeArchitectureHazardRuns.slice(0, 80).map((row) => compactTransportValue(row, "hazardRun", 1)) : compactTransportValue(workspace.codeArchitectureHazardRuns, "codeArchitectureHazardRuns", 1),
      safetyRemediation: compactTransportValue(workspace.safetyRemediation, "safetyRemediation", 1),
      reviewItems: Array.isArray(workspace.reviewItems) ? workspace.reviewItems.slice(0, 120).map((row) => compactTransportValue(row, "reviewItem", 1)) : compactTransportValue(workspace.reviewItems, "reviewItems", 1),
      safetyCases: Array.isArray(workspace.safetyCases) ? workspace.safetyCases.slice(0, 40).map((row) => compactTransportValue(row, "safetyCase", 1)) : compactTransportValue(workspace.safetyCases, "safetyCases", 1),
    },
  };
}

function trimTrailingSlashes(value) {
  return String(value || "").replace(/\/+$/g, "");
}

function resolveBaseUrl(baseUrl) {
  return trimTrailingSlashes(baseUrl || backendURL || "");
}

function resolveFetch(fetchImpl) {
  if (fetchImpl) return fetchImpl;
  if (typeof fetch === "function") return fetch;
  throw new Error("fetch is not available for MCP product client requests.");
}

async function readResponseBody(response) {
  try {
    return await response.json();
  } catch {
    try {
      const text = await response.text();
      return text ? { error: text } : {};
    } catch {
      return {};
    }
  }
}

function createMcpClientError(message, response, body) {
  const error = new Error(message);
  error.status = response?.status || 0;
  error.body = body || null;
  return error;
}

async function postMcpJson(path, payload = {}, options = {}) {
  const fetchImpl = resolveFetch(options.fetchImpl);
  const defaultHeaders = { "Content-Type": "application/json", ...(options.headers || {}) };
  const authOpts = buildAuthOpts(defaultHeaders) || {};
  let payloadForRequest = payload || {};
  let requestBody = JSON.stringify(payloadForRequest);
  if (path === "/api/mcp/projects/snapshot" && byteSize(requestBody) > MCP_SNAPSHOT_MAX_CLIENT_BODY_BYTES) {
    payloadForRequest = {
      ...payloadForRequest,
      snapshot: compactMcpSnapshotForTransport(payloadForRequest.snapshot || {}),
    };
    requestBody = JSON.stringify(payloadForRequest);
  }
  if (
    path === "/api/mcp/projects/snapshot" &&
    byteSize(requestBody) > MCP_SNAPSHOT_MAX_CLIENT_BODY_BYTES
  ) {
    throw new Error(`MCP snapshot is too large to publish (${(byteSize(requestBody) / 1024 / 1024).toFixed(1)} MB). Publish the active project snapshot or reduce generated workspace artifacts before publishing the full workspace.`);
  }
  const requestInit = {
    method: "POST",
    headers: authOpts?.headers || defaultHeaders,
    body: requestBody,
  };
  if (authOpts?.credentials) {
    requestInit.credentials = authOpts.credentials;
  }
  const response = await fetchImpl(`${resolveBaseUrl(options.baseUrl)}${path}`, requestInit);
  const body = await readResponseBody(response);

  if (!response.ok || body?.ok === false) {
    throw createMcpClientError(
      body?.error || `MCP product request failed (${response.status}).`,
      response,
      body
    );
  }

  return body;
}

function withUserExternalMcpConnectors(payload = {}, options = {}) {
  const userConnectors = Array.isArray(options.userConnectors)
    ? options.userConnectors
    : getUserExternalMcpConnectorsForBackend();
  if (!userConnectors.length) return payload;
  return {
    ...payload,
    userConnectors,
  };
}

async function getMcpJson(path, options = {}) {
  const fetchImpl = resolveFetch(options.fetchImpl);
  const authOpts = buildAuthOpts(options.headers || {}) || {};
  const requestInit = {
    method: "GET",
    headers: authOpts?.headers || options.headers || {},
  };
  if (authOpts?.credentials) {
    requestInit.credentials = authOpts.credentials;
  }
  const response = await fetchImpl(`${resolveBaseUrl(options.baseUrl)}${path}`, requestInit);
  const body = await readResponseBody(response);

  if (!response.ok || body?.ok === false) {
    throw createMcpClientError(
      body?.error || `MCP product request failed (${response.status}).`,
      response,
      body
    );
  }

  return body;
}

export function getMcpStatus(options = {}) {
  return getMcpJson("/api/mcp/status", options);
}

export function getMcpProjectSnapshotStatus(options = {}) {
  return getMcpJson("/api/mcp/projects/snapshot/status", options);
}

export async function listMcpTools(options = {}) {
  const body = await postMcpJson("/api/mcp/tools/list", {}, options);
  return body.tools || [];
}

export async function callMcpTool(name, args = {}, options = {}) {
  if (!name || typeof name !== "string") {
    throw new Error("MCP tool name is required.");
  }
  const body = await postMcpJson("/api/mcp/tools/call", {
    name,
    arguments: args || {},
  }, options);
  return body.result;
}

export function getMcpProjectContext(args = {}, options = {}) {
  return callMcpTool("get_project_context", args, options);
}

export function listMcpWorkflows(args = {}, options = {}) {
  return callMcpTool("list_available_workflows", args, options);
}

export function listMcpEngineeringArtifacts(args = {}, options = {}) {
  return callMcpTool("list_engineering_artifacts", args, options);
}

export function getMcpEngineeringContext(args = {}, options = {}) {
  return callMcpTool("get_engineering_context", args, options);
}

export async function summarizeMcpReviews(payload = {}, options = {}) {
  const body = await postMcpJson("/api/mcp/reviews/summary", payload, options);
  return {
    source: body.source,
    reviewItemCount: body.reviewItemCount,
    result: body.result,
  };
}

export async function publishMcpProjectSnapshot(snapshot = {}, options = {}) {
  return postMcpJson("/api/mcp/projects/snapshot", {
    snapshot: snapshot || {},
  }, options);
}

export async function listHostedMcpTokens(options = {}) {
  const body = await getMcpJson("/api/mcp/tokens", options);
  return body.tokens || [];
}

export async function createHostedMcpToken(payload = {}, options = {}) {
  const body = await postMcpJson("/api/mcp/tokens", {
    name: payload.name || "External MCP client",
    scopes: payload.scopes || ["mcp:read"],
    expiresAt: payload.expiresAt || null,
  }, options);
  return {
    token: body.token,
    tokenRecord: body.tokenRecord,
  };
}

export async function revokeHostedMcpToken(tokenId, options = {}) {
  if (!tokenId || typeof tokenId !== "string") {
    throw new Error("Hosted MCP token id is required.");
  }
  const body = await postMcpJson(`/api/mcp/tokens/${encodeURIComponent(tokenId)}/revoke`, {}, options);
  return body.token;
}

export async function discoverExternalMcpTools(options = {}) {
  const body = await postMcpJson("/api/mcp/external/tools/discover", withUserExternalMcpConnectors({}, options), options);
  return body.connectors || [];
}

export async function resolveExternalMcpCapabilities(options = {}) {
  const body = await postMcpJson("/api/mcp/external/capabilities", withUserExternalMcpConnectors({}, options), options);
  return body.connectors || [];
}

export async function callExternalMcpTool(connectorId, toolName, args = {}, options = {}) {
  if (!connectorId || typeof connectorId !== "string") {
    throw new Error("External MCP connector id is required.");
  }
  if (!toolName || typeof toolName !== "string") {
    throw new Error("External MCP tool name is required.");
  }
  const body = await postMcpJson("/api/mcp/external/tools/call", withUserExternalMcpConnectors({
    connectorId,
    toolName,
    arguments: args || {},
  }, options), options);
  return body.result;
}

export async function callExternalMcpToolWithContext(connectorId, toolName, args = {}, options = {}) {
  if (!connectorId || typeof connectorId !== "string") {
    throw new Error("External MCP connector id is required.");
  }
  if (!toolName || typeof toolName !== "string") {
    throw new Error("External MCP tool name is required.");
  }
  return postMcpJson("/api/mcp/external/tools/call", withUserExternalMcpConnectors({
    connectorId,
    toolName,
    arguments: args || {},
  }, options), options);
}

export async function callExternalMcpContextIntent(connectorId, intent, args = {}, options = {}) {
  if (!connectorId || typeof connectorId !== "string") {
    throw new Error("External MCP connector id is required.");
  }
  if (!intent || typeof intent !== "string") {
    throw new Error("External MCP context intent is required.");
  }
  return postMcpJson("/api/mcp/external/context/call", withUserExternalMcpConnectors({
    connectorId,
    intent,
    arguments: args || {},
  }, options), options);
}
